const AUTODESK_SETUP_PATH = "/rhumbix/autodesk-setup"
const AUTODESK_LICENSE_ERROR_PATH = "/rhumbix/autodesk-license-error"

/** The parsed body of a failed API response, as stored by the GET_CURRENT_USER_FAILED action. */
export interface FailedResponseBody {
    errors?: { can_provision_company?: boolean }
    can_provision_company?: boolean
}

const isPath = (currentPath: string, route: string): boolean => currentPath === route || currentPath === `${route}/`

/**
 * Decide where an Autodesk licensing denial (NO_AUTODESK_LICENSE / AUTODESK_LICENSE_UNAVAILABLE) sends the user.
 *
 * The backend marks a denial with `can_provision_company` when the denied user team-administers a Rhumbix-eligible
 * Autodesk team and their session was turned into a restricted one (WA-9830). Such a user is sent straight to the
 * company setup wizard, where they can provision a company on the team that licenses them. Everyone else goes to the
 * dead-end license error page.
 *
 * Once the user is in the wizard this returns null: the wizard re-seeds current_user, which clears the failed
 * response this decision reads, so it must not be derived again and bounce the user to the license error page.
 *
 * @param currentPath - The current location's pathname.
 * @param errorResponseData - The failed response body, with `can_provision_company` either wrapped in `errors` (the
 *     backend's exception handler) or at the top level.
 * @returns The route to navigate to, or null when the user is already where they belong.
 */
export function getAutodeskLicenseErrorRedirect(currentPath: string, errorResponseData?: FailedResponseBody | null): string | null {
    if (isPath(currentPath, AUTODESK_SETUP_PATH)) {
        return null
    }
    const canProvisionCompany = Boolean(errorResponseData?.errors?.can_provision_company ?? errorResponseData?.can_provision_company)
    const targetRoute = canProvisionCompany ? AUTODESK_SETUP_PATH : AUTODESK_LICENSE_ERROR_PATH
    return isPath(currentPath, targetRoute) ? null : targetRoute
}
